import { z } from 'zod';

import { UuidSchema } from '../id/id';
import { MAX_PAGE_SIZE } from '../pagination/pagination';

/**
 * A set of entity ids that arrives as ONE comma-joined query parameter.
 *
 * This exists for the admin kit's `ServerSelect`: a saved value has to render
 * its label even when the id is not on the page the picker happens to be
 * showing (W6). The alternatives are N+1 detail requests or preloading a
 * `pageSize=100` list — and W6 forbids the second by name.
 *
 * It is a **string**, not `z.array(UuidSchema)`, for the reason `BooleanParam`
 * is `'true'|'false'`: Express's `qs` parses one `?ids=x` into a bare string and
 * two into an array, so an array-shaped schema would accept a one-element
 * request only by accident. One spelling, no ambiguity (N7).
 *
 * Capped at `MAX_PAGE_SIZE` so an `ids` filter can never out-run the page it is
 * resolving against — the same ceiling, refused rather than clamped.
 */
export const IdsParamSchema = z
  .string()
  .transform((raw) =>
    raw
      .split(',')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0),
  )
  .pipe(z.array(UuidSchema).min(1).max(MAX_PAGE_SIZE));

export type IdsParam = z.infer<typeof IdsParamSchema>;
